import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import { komaIndexToTime } from '~/models/komaToTime'
import type { OverContractCell } from '~/pages/Supply48Data/hooks/useSupply48EditSession'
import { formatCellValue } from '~/pages/Supply48Data/models/cellValue'
import { protoDateToShortLabel } from '~/pages/Supply48Data/models/dateUtils'
import {
  type DisplayUnit,
  displayUnitLabelKey,
  toDisplayValue,
} from '~/pages/Supply48Data/models/displayUnit'

/** AC-S4D-018: 一覧で最初に出す件数。 残りは「ほか {M}件」 を押すと開く。 */
export const OVER_CONTRACT_LIST_LIMIT = 10

type Props = {
  cells: readonly OverContractCell[]
  displayUnit: DisplayUnit
  /** legId → 項目名 (列ヘッダーと同じ表示名) */
  itemLabelOf: (legId: string) => string
}

/**
 * AC-S4D-018: 契約量を超えるセルを 1 セル 1 行で並べる。
 * 先頭 10 件まで出し、 11 件以上ある時は末尾の「ほか {M}件」 で残りを開く
 * (`conventions.md`「警告・案内で対象を列挙する時の件数と上限」)。
 */
export const OverContractCellList = ({
  cells,
  displayUnit,
  itemLabelOf,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? cells : cells.slice(0, OVER_CONTRACT_LIST_LIMIT)
  const hiddenCount = cells.length - visible.length
  const unit = t(displayUnitLabelKey(displayUnit))

  return (
    <Box as="ul" marginTop="2" listStyleType="none">
      {visible.map((cell) => {
        const plan = toDisplayValue(cell.storedKwh, displayUnit) ?? 0
        const contract = toDisplayValue(cell.contractKwh, displayUnit) ?? 0
        return (
          <li
            key={`${cell.legId}:${cell.deliveryDate.year}-${cell.deliveryDate.month}-${cell.deliveryDate.day}:${cell.komaIdx}`}
          >
            <Text textStyle="body-sm">
              {t('page:supply48Data.overContract.item', {
                item: itemLabelOf(cell.legId),
                date: protoDateToShortLabel(cell.deliveryDate),
                koma: komaIndexToTime(cell.komaIdx),
                plan: formatCellValue(plan),
                contract: formatCellValue(contract),
                excess: formatCellValue(plan - contract),
                unit,
              })}
            </Text>
          </li>
        )
      })}
      {hiddenCount > 0 && (
        <li>
          <TertiaryButton onClick={() => setExpanded(true)} size="sm">
            {t('page:supply48Data.overContract.more', { count: hiddenCount })}
          </TertiaryButton>
        </li>
      )}
    </Box>
  )
}
